import type {ReactNode} from 'react';
import {cn} from '../lib/cn';
import {RatingRadioGroupProvider} from './RatingRadioGroupContext';
import {RatingRadioGroupItem} from './RatingRadioGroupItem';
import {RatingRadioGroupLegend} from './RatingRadioGroupLegend';
import {RatingRadioGroupList} from './RatingRadioGroupList';
import type {RatingRadioGroupOption, RatingRadioGroupSize} from './types';
import {useRatingPreview} from './useRatingPreview';

export type RatingRadioGroupProps = {
  value?: number;
  onChange?: (value: number) => void;
  label?: ReactNode;
  disabled?: boolean;
  name?: string;
  className?: string;
  options?: RatingRadioGroupOption[];
  size?: RatingRadioGroupSize;
  children?: ReactNode;
};

function RatingRadioGroupRoot({
  value,
  onChange,
  label,
  name = 'rating',
  className,
  options,
  size = 'default',
  children,
}: RatingRadioGroupProps) {
  const {
    hoverValue,
    handleContainerMouseLeave,
    handleStarMouseEnter,
    handleTouchStart,
    handleTouchEnd,
  } = useRatingPreview();

  return (
    <RatingRadioGroupProvider
      value={{
        name,
        value: value || 0,
        size,
        hoverValue,
        onChange,
        onMouseLeave: handleContainerMouseLeave,
        onMouseEnter: handleStarMouseEnter,
        onTouchStart: handleTouchStart,
        onTouchEnd: handleTouchEnd,
      }}
    >
      <fieldset className={cn('relative [all:unset]', className)}>
        {children ?? (
          <>
            <RatingRadioGroup.Legend>{label}</RatingRadioGroup.Legend>
            <RatingRadioGroup.List>
              {options?.map(option => (
                <RatingRadioGroup.Item key={option.value} option={option} />
              ))}
            </RatingRadioGroup.List>
          </>
        )}
      </fieldset>
    </RatingRadioGroupProvider>
  );
}

export const RatingRadioGroup = Object.assign(RatingRadioGroupRoot, {
  Legend: RatingRadioGroupLegend,
  List: RatingRadioGroupList,
  Item: RatingRadioGroupItem,
});
